import React, { useMemo, useState } from 'react';
import { Box, Button, ButtonBase, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LONG_VALUE_THRESHOLD, useStyles } from './CallAnalysisJsonTree.styles';

type JsonPrimitive = string | number | boolean | null;
interface JsonObject {
  [key: string]: JsonValue;
}

interface JsonArray extends Array<JsonValue> {}

type JsonCollection = JsonArray | JsonObject;
type JsonValue = JsonPrimitive | JsonCollection;

interface CallAnalysisJsonTreeProps {
  value: unknown;
  onCopyJson: () => void;
}

interface PrimitiveRowProps {
  label: string;
  value: JsonPrimitive;
}

interface TreeNodeProps {
  value: JsonValue;
  label: string;
  nodeId: string;
  depth?: number;
  expandedPaths: Set<string>;
  onTogglePath: (path: string) => void;
}

const isCollection = (value: JsonValue): value is JsonCollection =>
  typeof value === 'object' && value !== null;

const getEntries = (value: JsonCollection) =>
  Array.isArray(value)
    ? value.map((item, index) => [`[${index}]`, item] as const)
    : Object.entries(value);

const getCollectionCount = (value: JsonCollection) =>
  Array.isArray(value) ? value.length : Object.keys(value).length;

const collectExpandablePaths = (
  value: JsonValue,
  currentPath = '',
  paths: Set<string> = new Set(),
): Set<string> => {
  if (!isCollection(value)) return paths;

  for (const [key, childValue] of getEntries(value)) {
    const nextPath = currentPath ? `${currentPath}.${key}` : key;
    if (isCollection(childValue)) {
      paths.add(nextPath);
      collectExpandablePaths(childValue, nextPath, paths);
    }
  }

  return paths;
};

const formatPrimitiveValue = (value: JsonPrimitive) => {
  if (typeof value === 'string') return value;
  if (value === null) return 'null';
  return String(value);
};

const PrimitiveRow: React.FC<PrimitiveRowProps> = ({ label, value }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const formattedValue = formatPrimitiveValue(value);
  const isLongValue = typeof value === 'string' && formattedValue.length > LONG_VALUE_THRESHOLD;
  const visibleValue =
    isLongValue && !isExpanded
      ? `${formattedValue.slice(0, LONG_VALUE_THRESHOLD).trimEnd()}...`
      : formattedValue;

  return (
    <Box className={classes.primitiveRow}>
      <Box />
      <Stack spacing={0.5} className={classes.primitiveContent}>
        <Typography variant="body2" className={classes.primitiveValue}>
          <Box component="span" className={classes.primitiveLabel}>
            {label}:
          </Box>{' '}
          {visibleValue}
        </Typography>
        {isLongValue ? (
          <Typography
            className={classes.primitiveToggle}
            onClick={() => setIsExpanded((previousState) => !previousState)}>
            {t(
              isExpanded
                ? 'campaignPage.callAnalysis.viewer.showLess'
                : 'campaignPage.callAnalysis.viewer.showMore',
            )}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
};

const TreeNode: React.FC<TreeNodeProps> = ({
  value,
  label,
  nodeId,
  depth = 0,
  expandedPaths,
  onTogglePath,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  if (!isCollection(value)) {
    return <PrimitiveRow label={label} value={value} />;
  }

  const entries = getEntries(value);

  if (depth === 0) {
    if (!entries.length) {
      return (
        <Typography variant="body2" className={classes.emptyState}>
          {Array.isArray(value) ? '[]' : '{}'}
        </Typography>
      );
    }

    return (
      <Stack spacing={0.25}>
        {entries.map(([childLabel, childValue]) => (
          <TreeNode
            key={childLabel}
            value={childValue}
            label={childLabel}
            nodeId={childLabel}
            depth={depth + 1}
            expandedPaths={expandedPaths}
            onTogglePath={onTogglePath}
          />
        ))}
      </Stack>
    );
  }

  const isExpanded = expandedPaths.has(nodeId);
  const itemCount = getCollectionCount(value);

  return (
    <Box style={depth > 1 ? { paddingLeft: depth * 6 } : undefined}>
      <ButtonBase
        onClick={() => onTogglePath(nodeId)}
        className={cx(classes.branchButton, isExpanded && classes.branchButtonExpanded)}>
        <Typography variant="body2" className={classes.branchChevron}>
          {isExpanded ? '▾' : '▸'}
        </Typography>
        <Typography
          variant="body2"
          className={cx(classes.branchTitle, isExpanded && classes.branchTitleExpanded)}>
          {label}
        </Typography>
        <Typography variant="body2" className={classes.branchMeta}>
          {t('campaignPage.callAnalysis.viewer.itemsCount', { count: itemCount })}
        </Typography>
      </ButtonBase>

      {isExpanded ? (
        <Stack className={classes.children}>
          {entries.map(([childLabel, childValue]) => {
            const childNodeId = `${nodeId}.${childLabel}`;

            return (
              <TreeNode
                key={childNodeId}
                value={childValue}
                label={childLabel}
                nodeId={childNodeId}
                depth={depth + 1}
                expandedPaths={expandedPaths}
                onTogglePath={onTogglePath}
              />
            );
          })}
        </Stack>
      ) : null}
    </Box>
  );
};

export const CallAnalysisJsonTree: React.FC<CallAnalysisJsonTreeProps> = ({
  value,
  onCopyJson,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

  const normalizedValue = value as JsonValue;
  const allExpandablePaths = useMemo(
    () => collectExpandablePaths(normalizedValue),
    [normalizedValue],
  );

  const handleTogglePath = (path: string) => {
    setExpandedPaths((previousState) => {
      const nextState = new Set(previousState);

      if (nextState.has(path)) nextState.delete(path);
      else nextState.add(path);

      return nextState;
    });
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} className={classes.actions}>
        <Button variant="outlined" size="small" onClick={onCopyJson}>
          {t('campaignPage.callAnalysis.viewer.copyJson')}
        </Button>
        <Button
          variant="text"
          size="small"
          onClick={() => setExpandedPaths(new Set(allExpandablePaths))}>
          {t('campaignPage.callAnalysis.viewer.expandAll')}
        </Button>
        <Button variant="text" size="small" onClick={() => setExpandedPaths(new Set())}>
          {t('campaignPage.callAnalysis.viewer.collapseAll')}
        </Button>
      </Stack>

      <Box className={classes.viewer}>
        <TreeNode
          value={normalizedValue}
          label="root"
          nodeId="root"
          depth={0}
          expandedPaths={expandedPaths}
          onTogglePath={handleTogglePath}
        />
      </Box>
    </Stack>
  );
};
